///
/// EarthBrowser
///
/// Created by Matt Giger on 10/12/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Views / Share cluster for the current view, in the top-right beside the
/// account control. Both act on app state (camera + visible layers +
/// focused entity), which is why they live here rather than in the camera
/// stack. The bookmark is a split button: the icon saves (⌘D - instant
/// save plus the rename toast in the notification slot), the chevron opens
/// the Views menu with the saved views, places, stories and recents (the
/// browser-bookmarks pattern). Share copies a link to what's on screen
/// (short server link when signed in, self-contained param URL otherwise)
/// and uses the native share sheet where one exists.
///

import { useShareLink } from "@/hooks/useShareLink"
import { track } from "@/utils/analytics"
import { isDevelopment } from "@/utils/environment"
import { saveCurrentView } from "@/utils/savedViews"
import { cn } from "@/utils/utils"
import { BookmarkIcon, CheckIcon, Share2Icon } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { RenderSceneMenu } from "./RenderSceneMenu"
import { ViewsMenu } from "./ViewsMenu"

const cCopiedFeedbackMs = 1500

export function ViewActions({ className }: { className?: string }) {
	const { shareCurrentView } = useShareLink()
	const [sharing, setSharing] = useState(false)
	const [copied, setCopied] = useState(false)
	const copiedTimer = useRef<number | null>(null)

	useEffect(() => () => {
		if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current)
	}, [])

	const handleShare = async () => {
		if (sharing) return
		setSharing(true)
		try {
			const url = await shareCurrentView()
			// Phones: the native sheet (Messages, Mail, ...). Elsewhere, or
			// when the user cancels the sheet, fall back to the clipboard
			let shared = false
			if (typeof navigator.share === "function") {
				try {
					await navigator.share({ url })
					shared = true
				} catch (error) {
					if ((error as DOMException)?.name === "AbortError") return
				}
			}
			if (!shared) {
				await navigator.clipboard.writeText(url)
				setCopied(true)
				if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current)
				copiedTimer.current = window.setTimeout(() => setCopied(false), cCopiedFeedbackMs)
			}
			track("view_shared", { method: shared ? "native" : "clipboard" })
		} finally {
			setSharing(false)
		}
	}

	const buttonClass = cn(
		"flex items-center justify-center h-full w-9 text-white/80 hover:text-white hover:bg-white/10",
		"transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
	)

	return (
		<div
			className={cn(
				"flex items-center h-9 bg-black/40 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl overflow-hidden",
				className
			)}
			data-view-actions
		>
			{/* Bookmark split button: save | open the Views menu */}
			<button
				type="button"
				onClick={() => { void saveCurrentView() }}
				className={cn(buttonClass, "w-8 pl-1")}
				title="Save this view (⌘D)"
				aria-label="Save this view"
			>
				<BookmarkIcon className="w-4 h-4" />
			</button>
			<ViewsMenu triggerClassName={cn(buttonClass, "w-6 pr-1 text-white/60")} />
			<div className="w-px self-stretch my-1.5 bg-white/20" />
			{/* Video scene export: development builds only (an ops tool, like __ebDebug) */}
			{isDevelopment() && (
				<>
					<RenderSceneMenu triggerClassName={buttonClass} />
					<div className="w-px self-stretch my-1.5 bg-white/20" />
				</>
			)}
			<button
				type="button"
				onClick={() => { void handleShare() }}
				disabled={sharing}
				className={cn(buttonClass, copied && "text-green-300 hover:text-green-200")}
				title={copied ? "Link copied" : "Share a link to this view"}
				aria-label={copied ? "Link copied" : "Share this view"}
			>
				{copied ? <CheckIcon className="w-4 h-4" /> : <Share2Icon className="w-4 h-4" />}
			</button>
		</div>
	)
}
